import { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-hot-toast'

import { QUERY_KEYS } from '@/lib/cache-keys/queryKeys'
import { walletApi } from 'api/walletApi'
import { subscribeToWalletSocket } from 'hooks/useWalletSocket'

const UNCONFIRMED_TIMEOUT_MS = 60_000
const RECENT_TRANSACTIONS_LIMIT = 10

export type DepositSyncStatus = 'idle' | 'polling' | 'credited' | 'failed' | 'unconfirmed'

// The balance itself is refreshed by the page's balance query and by the wallet socket
// (on auth.ok and every event) - this hook only works out whether the top-up has settled.
// preTopUpTransactionId comes from the return URL (see PRE_TOP_UP_TRANSACTION_ID_PARAM).
export function useDepositBalanceSync(enabled: boolean, preTopUpTransactionId: number | null) {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<DepositSyncStatus>('idle')
  // True once the deposit has been checked (or an event settled it first).
  const [hasCheckedOnce, setHasCheckedOnce] = useState(false)

  useEffect(() => {
    if (!enabled) return

    let cancelled = false
    let settled = false
    let checking = false
    let recheckRequested = false
    let unconfirmedTimer: ReturnType<typeof setTimeout> | undefined

    // Without a snapshot (it couldn't be taken), any deposit event counts - see checkDeposit.
    const isTopUpTransaction = (id: number) =>
      preTopUpTransactionId === null || id > preTopUpTransactionId

    const finish = (result: 'credited' | 'failed') => {
      if (settled) return
      settled = true
      clearTimeout(unconfirmedTimer)
      setHasCheckedOnce(true)
      setStatus(result)

      if (result === 'failed') {
        toast.error("Your deposit didn't go through. Your balance hasn't changed.", {
          duration: 6000,
        })
      }
    }

    // Runs on mount, independently of the socket, and again on every auth.ok - the socket
    // has no replay, so a settlement that happened while it was disconnected is only
    // picked up here.
    const checkDeposit = async () => {
      if (settled) return
      // Don't drop a re-check that arrives mid-check: the deposit may have settled since
      // the in-flight request was sent.
      if (checking) {
        recheckRequested = true
        return
      }

      // Without a snapshot the list can't tell the top-up from an older, already settled
      // deposit, so leave settling to the socket event.
      if (preTopUpTransactionId === null) {
        setHasCheckedOnce(true)
        return
      }

      checking = true
      const transactions = await walletApi
        .getWalletTransactions({ limit: RECENT_TRANSACTIONS_LIMIT })
        .catch(() => null)
      checking = false
      if (cancelled || settled) return

      setHasCheckedOnce(true)

      const deposit = transactions?.find(
        (transaction) => transaction.type === 'DEPOSIT' && isTopUpTransaction(transaction.id),
      )
      if (deposit && deposit.status !== 'pending') {
        queryClient.invalidateQueries([QUERY_KEYS.WALLET_TRANSACTIONS])
        finish(deposit.status === 'completed' ? 'credited' : 'failed')
        return
      }

      if (recheckRequested) {
        recheckRequested = false
        checkDeposit()
      }
    }

    // Subscribe before the first check, so an event landing mid-check isn't missed.
    const unsubscribe = subscribeToWalletSocket((message) => {
      if (message.type === 'auth.ok') {
        checkDeposit()
        return
      }
      // The socket has already refetched the balance and transactions for this event.
      if (message.transaction_type !== 'DEPOSIT') return
      if (!isTopUpTransaction(message.transaction_id)) return
      if (message.status === 'completed') finish('credited')
      else if (message.status === 'failed') finish('failed')
    })

    setHasCheckedOnce(false)
    setStatus('polling')
    checkDeposit()

    unconfirmedTimer = setTimeout(() => {
      if (settled) return
      setStatus('unconfirmed')
      toast('Your deposit is still processing - your balance will update shortly.', {
        duration: 6000,
      })
    }, UNCONFIRMED_TIMEOUT_MS)

    return () => {
      cancelled = true
      clearTimeout(unconfirmedTimer)
      unsubscribe()
    }
  }, [enabled, preTopUpTransactionId, queryClient])

  return { status, hasCheckedOnce }
}
